import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Upload, Table, Tag, Space, Popconfirm, Typography, Alert, Progress, Empty } from "antd";
import {
  InboxOutlined,
  DeleteOutlined,
  DownloadOutlined,
  PlayCircleOutlined,
} from "@ant-design/icons";
import Card from "../../card/Card";
import { Button, Toast } from "../../ui";
import { useTheme } from "../../../contexts/ThemeContext";
import ExcelAiService from "../../../services/excelAiService";

const { Text } = Typography;

const EXTENSOES = ".xlsx,.xls,.csv";

const formatarTamanho = (bytes) => {
  if (!bytes) return "-";
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

const formatarData = (iso) => {
  if (!iso) return "-";
  try {
    return new Date(iso).toLocaleString("pt-BR", {
      day: "2-digit",
      month: "2-digit",
      year: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return "-";
  }
};

/**
 * Arquivos do grupo de uma sincronização Excel com IA.
 *
 * Diferente do Excel V1, aqui o upload é independente do "salvar" da sincronização: o arquivo sobe
 * na hora e vira uma linha própria. Por isso não existe o aviso em caixa-alta do V1 mandando salvar
 * a Sync depois de anexar.
 */
const ExcelAiFilesCard = ({
  syncId,
  podeEditar = true,
  processando = false,
  onProcessar,
  // Salva a empresa quando ainda não existe e devolve o id (ou null se a validação barrar).
  onGarantirSalvo,
  onRejeitados,
}) => {
  const { theme } = useTheme();
  const [arquivos, setArquivos] = useState([]);
  const [carregando, setCarregando] = useState(false);
  const [enviando, setEnviando] = useState(false);
  const [progresso, setProgresso] = useState(0);
  const [rejeitados, setRejeitados] = useState([]);

  // Só a listagem mais recente vale: logo após o auto-save, a troca de `syncId` dispara uma
  // listagem que pode responder depois da que roda ao fim do upload, já com o arquivo.
  const ultimaListagem = useRef(0);

  const carregar = useCallback(async (id = syncId) => {
    const chamada = ++ultimaListagem.current;
    if (!id) { setArquivos([]); return; }
    try {
      setCarregando(true);
      const lista = await ExcelAiService.listarArquivos(id);
      if (chamada === ultimaListagem.current) setArquivos(lista);
    } catch (erro) {
      console.error("Erro ao listar arquivos do Excel com IA:", erro);
      Toast.error("Não foi possível carregar os arquivos.");
    } finally {
      setCarregando(false);
    }
  }, [syncId]);

  useEffect(() => {
    carregar();
  }, [carregar]);

  const novos = useMemo(
    () => arquivos.filter((a) => a.situacao !== "Processado").length,
    [arquivos],
  );

  /**
   * Acumula o `beforeUpload` de cada arquivo num único POST. O antd chama o handler uma vez por
   * arquivo, e mandar N requisições separadas quebraria a validação de limite por empresa, que é
   * avaliada no servidor a cada lote.
   */
  const enviarLote = useMemo(() => {
    let acumulados = [];
    let agendado = null;

    return (arquivo) => {
      acumulados.push(arquivo);
      if (agendado) clearTimeout(agendado);

      agendado = setTimeout(async () => {
        const lote = acumulados;
        acumulados = [];
        agendado = null;

        try {
          setEnviando(true);
          setProgresso(0);
          setRejeitados([]);

          // Sem empresa ainda: salva primeiro e envia na sequência, para o usuário não precisar
          // salvar, sair e voltar só para anexar. Se a validação barrar (nome, projeto...), o
          // próprio save já avisou e o lote é descartado: nada sobe sem uma empresa salva.
          let id = syncId;
          if (!id) {
            id = await onGarantirSalvo?.();
            if (!id) return;
          }

          const resultado = await ExcelAiService.anexarArquivos(id, lote, setProgresso);

          if (resultado.rejeitados?.length) onRejeitados?.(resultado.rejeitados);

          if (resultado.aceitos?.length) {
            Toast.success(
              resultado.aceitos.length === 1
                ? "1 arquivo anexado."
                : `${resultado.aceitos.length} arquivos anexados.`,
            );
          }
          if (resultado.rejeitados?.length) setRejeitados(resultado.rejeitados);

          await carregar(id);
        } catch (erro) {
          console.error("Erro ao anexar arquivos:", erro);
          Toast.error("Falha ao enviar os arquivos.");
        } finally {
          setEnviando(false);
          setProgresso(0);
        }
      }, 100);

      return false; // impede o upload automático do antd
    };
  }, [syncId, carregar, onGarantirSalvo, onRejeitados]);

  const remover = async (fileId) => {
    try {
      await ExcelAiService.removerArquivo(fileId);
      Toast.success("Arquivo removido.");
      await carregar();
    } catch (erro) {
      console.error("Erro ao remover arquivo:", erro);
      Toast.error("Não foi possível remover o arquivo.");
    }
  };

  const baixar = async (fileId, nome) => {
    try {
      const { url } = await ExcelAiService.baixarArquivo(fileId);
      if (!url) throw new Error("sem url");
      const link = document.createElement("a");
      link.href = url;
      link.download = nome || "arquivo";
      link.target = "_blank";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (erro) {
      console.error("Erro ao baixar arquivo:", erro);
      Toast.error("Não foi possível baixar o arquivo.");
    }
  };

  const colunas = [
    {
      title: "Arquivo",
      dataIndex: "nomeOriginal",
      key: "nomeOriginal",
      ellipsis: true,
      render: (nome) => <Text style={{ fontSize: 13 }}>{nome}</Text>,
    },
    {
      title: "Tamanho",
      dataIndex: "tamanhoBytes",
      key: "tamanhoBytes",
      width: 100,
      render: formatarTamanho,
    },
    {
      title: "Enviado por",
      dataIndex: "uploadedByEmail",
      key: "uploadedByEmail",
      ellipsis: true,
      responsive: ["lg"],
    },
    {
      title: "Data",
      dataIndex: "uploadedAt",
      key: "uploadedAt",
      width: 140,
      render: (valor) => (valor ? formatarData(valor) : "—"),
    },
    {
      title: "Situação",
      dataIndex: "situacao",
      key: "situacao",
      width: 120,
      render: (situacao) => {
        if (situacao === "Processado") return <Tag>Processado</Tag>;
        return <Tag color="blue">{situacao}</Tag>;
      },
    },
    {
      title: "",
      key: "acoes",
      width: 90,
      render: (_, registro) => (
        <Space size={4}>
          <Button
            type="text"
            size="small"
            icon={<DownloadOutlined />}
            onClick={() => baixar(registro.id, registro.nomeOriginal)}
          />
          {podeEditar && (
            <Popconfirm
              title="Remover este arquivo?"
              description="Os dados já carregados continuam; sai apenas o arquivo original."
              okText="Remover"
              cancelText="Cancelar"
              onConfirm={() => remover(registro.id)}
            >
              <Button type="text" size="small" danger icon={<DeleteOutlined />} />
            </Popconfirm>
          )}
        </Space>
      ),
    },
  ];

  return (
    <Card
      title={
        <Space>
          <span style={{ fontSize: 16, fontWeight: 600 }}>Arquivos da integração</span>
          {novos > 0 && (
            <Tag color="blue">
              {novos === 1 ? "1 arquivo não processado" : `${novos} arquivos não processados`}
            </Tag>
          )}
        </Space>
      }
      extra={
        podeEditar && (
          <Button
            type="primary"
            icon={<PlayCircleOutlined />}
            loading={processando}
            disabled={arquivos.length === 0 || processando || enviando}
            onClick={onProcessar}
            style={{ backgroundColor: theme.primary, borderColor: theme.primary }}
          >
            Processar arquivos
          </Button>
        )
      }
      style={{ marginBottom: 24 }}
    >
      <Text style={{ display: "block", marginBottom: 16, color: theme.textSecondary, fontSize: 14 }}>
        Envie as planilhas como você as tem. O sistema identifica o que é cada arquivo, normaliza os
        dados e avisa o que precisar da sua decisão — não existe modelo a preencher.
      </Text>

      {podeEditar && (
        <Upload.Dragger
          multiple
          accept={EXTENSOES}
          beforeUpload={enviarLote}
          showUploadList={false}
          disabled={enviando}
          style={{ marginBottom: 16, background: theme.background }}
        >
          <p className="ant-upload-drag-icon" style={{ marginBottom: 8 }}>
            <InboxOutlined style={{ fontSize: 36, color: theme.primary }} />
          </p>
          <p style={{ margin: 0, fontSize: 14 }}>
            Arraste os arquivos aqui ou clique para selecionar
          </p>
          <p style={{ margin: 0, fontSize: 12, color: theme.textSecondary }}>
            .xlsx, .xls ou .csv — vários de uma vez
          </p>
        </Upload.Dragger>
      )}

      {enviando && <Progress percent={progresso} size="small" style={{ marginBottom: 16 }} />}

      {rejeitados.length > 0 && (
        <Alert
          type="warning"
          showIcon
          style={{ marginBottom: 16 }}
          message={
            rejeitados.length === 1
              ? "1 arquivo não foi aceito"
              : `${rejeitados.length} arquivos não foram aceitos`
          }
          description={
            <ul style={{ margin: 0, paddingLeft: 18 }}>
              {rejeitados.map((r, i) => (
                <li key={`${r.nomeOriginal}-${i}`} style={{ fontSize: 13 }}>
                  <b>{r.nomeOriginal}</b>: {r.motivo}
                </li>
              ))}
            </ul>
          }
          closable
          onClose={() => setRejeitados([])}
        />
      )}

      <Table
        size="small"
        rowKey="id"
        loading={carregando}
        columns={colunas}
        dataSource={arquivos}
        pagination={false}
        locale={{
          emptyText: (
            <Empty
              image={Empty.PRESENTED_IMAGE_SIMPLE}
              description="Nenhum arquivo anexado ainda"
            />
          ),
        }}
      />
    </Card>
  );
};

export default ExcelAiFilesCard;
